import React, { useState, useEffect } from "react";
import { Container, Card, Row, Col, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import { resolveDefaultSeasonForLeague } from "../utils/activeSeasonUtil";

function ManagerGameListPage() {
    const [games, setGames] = useState([]);
    const [leagues, setLeagues] = useState([]);
    const [seasons, setSeasons] = useState([]);
    const [selectedLeague, setSelectedLeague] = useState(null);
    const [selectedSeason, setSelectedSeason] = useState(null);
    /** False until active-season API (or fallback) has chosen the default for the current league */
    const [seasonDefaultReady, setSeasonDefaultReady] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch leagues on mount only
    useEffect(() => {
        axios
            .get('/api/leagues/active-leagues/', {
                withCredentials: true,
            })
            .then((response) => {
                setLeagues(response.data);
                if (response.data.length > 0) {
                    setSelectedLeague(response.data[0]);
                }
            })
            .catch((error) => {
                console.error("Error fetching leagues:", error);
                setError("Failed to load leagues");
            });
    }, []);

    // Default season for the selected league (active-season window via API)
    useEffect(() => {
        if (!selectedLeague) {
            setSeasonDefaultReady(false);
            return;
        }
        setGames([]);
        const allSeasons = selectedLeague.seasons || [];
        setSeasons(allSeasons);
        setSeasonDefaultReady(false);
        let cancelled = false;
        resolveDefaultSeasonForLeague(selectedLeague).then((season) => {
            if (cancelled) return;
            setSelectedSeason(season);
            setSeasonDefaultReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, [selectedLeague]);

    // Fetch games only when selectedLeague or selectedSeason changes
    useEffect(() => {
        if (!selectedLeague) {
            setLoading(false);
            return;
        }
        if (!seasonDefaultReady) {
            setLoading(true);
            return;
        }

        setLoading(true);
        let url = `/api/games/leagues/${selectedLeague.id}/`;
        const params = new URLSearchParams();

        if (selectedSeason) {
            params.append('season_id', selectedSeason.id);
        }

        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        axios
            .get(url, {
                withCredentials: true,
            })
            .then((response) => {
                setGames(response.data);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching games:", error);
                setError("Failed to load games");
                setLoading(false);
            });
    }, [selectedLeague, selectedSeason, seasonDefaultReady]);

    const handleLeagueChange = (leagueId) => {
        const league = leagues.find((l) => l.id === Number(leagueId));
        if (league) setSelectedLeague(league);
    };

    const handleSeasonChange = (seasonId) => {
        if (seasonId === "" || seasonId == null) {
            setSelectedSeason(null);
            return;
        }
        const season = seasons.find((s) => s.id === Number(seasonId));
        setSelectedSeason(season ?? null);
    };

    if (loading && games.length === 0) {
        return (
            <Container className="p-0">
                <Card className="h-100">
                    <Card.Body>
                        <p>Loading games...</p>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="p-0">
                <Card className="h-100">
                    <Card.Body>
                        <p className="text-danger">{error}</p>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="p-0">
            <Card className="h-100">
                {/* Card Header */}
                <Card.Header className="d-sm-flex align-items-center text-center justify-content-sm-between border-0 pb-0">
                    <h1 className="h4 card-title">Games</h1>

                    {/* Filter Controls */}
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        <Form.Select
                            id="league-game-filter"
                            className="form-select"
                            style={{ minWidth: "10rem", maxWidth: "100%" }}
                            aria-label="League"
                            value={selectedLeague?.id ?? ""}
                            onChange={(e) => handleLeagueChange(e.target.value)}
                        >
                            {leagues.map((league) => (
                                <option key={league.id} value={league.id}>
                                    {league.name}
                                </option>
                            ))}
                        </Form.Select>

                        {seasons.length > 0 && (
                            <Form.Select
                                id="season-game-filter"
                                className="form-select"
                                style={{ minWidth: "10rem", maxWidth: "100%" }}
                                aria-label="Season"
                                value={selectedSeason ? String(selectedSeason.id) : ""}
                                onChange={(e) => handleSeasonChange(e.target.value)}
                            >
                                <option value="">All Seasons</option>
                                {seasons.map((season) => (
                                    <option key={season.id} value={season.id}>
                                        {season.name}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </div>
                </Card.Header>

                {/* Card Body */}
                <Card.Body>
                    <Container>
                        <Row className="g-4">
                            {games.map((game) => {
                                // Extract team participants from the new API structure
                                const homeParticipant =
                                    game.teams?.find((p) => p.is_home) || {};
                                const awayParticipant =
                                    game.teams?.find((p) => p.is_away) || {};

                                const isByeGame = game.game_type === 'bye';

                                return (
                                    <Col key={game.id} className="col-sm-6 col-xl-4">
                                        <Card
                                            className="h-100 shadow-sm position-relative"
                                            style={{
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-5px)';
                                                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                            }}
                                        >
                                            <div className="position-relative">
                                                {/* Game Type Badge - positioned above the image */}
                                                <div className="position-absolute top-0 start-0 m-2" style={{ zIndex: 10 }}>
                                                    <span className={`badge ${game.game_type === 'preseason' ? 'bg-info' :
                                                        game.game_type === 'regular' ? 'bg-primary' :
                                                            game.game_type === 'makeup' ? 'bg-warning' :
                                                                game.game_type === 'playoff' ? 'bg-success' : 'bg-secondary'
                                                        }`}>
                                                        {game.game_type ? game.game_type.charAt(0).toUpperCase() + game.game_type.slice(1) : 'Unknown'}
                                                    </span>
                                                </div>
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: '200px',
                                                        position: 'relative',
                                                        overflow: 'hidden',
                                                        borderRadius: '8px 8px 0 0',
                                                    }}
                                                >
                                                    {isByeGame ? (
                                                        // For bye games, show only home team logo
                                                        homeParticipant.participant_logo ? (
                                                            <img
                                                                src={homeParticipant.participant_logo}
                                                                alt={`${homeParticipant.participant_name} logo`}
                                                                style={{
                                                                    width: '100%',
                                                                    height: '200px',
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        ) : (
                                                            // Placeholder for bye games
                                                            <div
                                                                style={{
                                                                    width: '100%',
                                                                    height: '200px',
                                                                    backgroundColor: '#f8f9fa',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <i className="bi bi-people-fill" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                                                            </div>
                                                        )
                                                    ) : (
                                                        // For regular games, show split image
                                                        <>
                                                            {homeParticipant.participant_logo ? (
                                                                // Top half - Home Team logo
                                                                <img
                                                                    src={homeParticipant.participant_logo}
                                                                    alt={`${homeParticipant.participant_name} logo`}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '200px',
                                                                        objectFit: 'cover',
                                                                        position: 'absolute',
                                                                        left: 0,
                                                                        top: 0,
                                                                        clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
                                                                    }}
                                                                />
                                                            ) : (
                                                                // Placeholder for top half - Blue
                                                                <div
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '50%',
                                                                        backgroundColor: 'blue',
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                    }}
                                                                ></div>
                                                            )}
                                                            {awayParticipant.participant_logo ? (
                                                                // Bottom half - Away Team logo
                                                                <img
                                                                    src={awayParticipant.participant_logo}
                                                                    alt={`${awayParticipant.participant_name} logo`}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '200px',
                                                                        objectFit: 'cover',
                                                                        position: 'absolute',
                                                                        left: 0,
                                                                        bottom: 0,
                                                                        clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
                                                                    }}
                                                                />
                                                            ) : (
                                                                // Placeholder for bottom half - Red
                                                                <div
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '50%',
                                                                        backgroundColor: 'red',
                                                                        position: 'absolute',
                                                                        bottom: 0,
                                                                    }}
                                                                ></div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                                <div className={`badge ${game.is_played ? 'bg-success' : 'bg-danger'} text-white mt-2 me-2 position-absolute top-0 end-0`}>
                                                    {game.is_played ? 'Played' : 'Upcoming'}
                                                </div>
                                            </div>
                                            <Card.Body className="position-relative pt-0">
                                                <div className="btn btn-xs btn-primary mt-n3">
                                                    {game.league_name}
                                                </div>
                                                {isByeGame ? (
                                                    // Simplified display for bye games
                                                    <div className="text-center mb-2">
                                                        <h6 className="mt-3">
                                                            <Link className="nav-link" to={`/league-manager/games/${game.id}/`}>
                                                                {homeParticipant.participant_name || 'Team'} (BYE)
                                                            </Link>
                                                        </h6>
                                                    </div>
                                                ) : (
                                                    // Regular game display
                                                    <h6 className="mt-3">
                                                        <Link className="nav-link" to={`/league-manager/games/${game.id}/`}>
                                                            {game.home_team} vs. {game.away_team}
                                                        </Link>
                                                    </h6>
                                                )}
                                                <p className="mb-0 small">
                                                    <i className="bi bi-calendar-check pe-1"></i>{" "}
                                                    {game.scheduled_date_only} at{" "}
                                                    {game.scheduled_time_only}
                                                </p>
                                                <p className="small">
                                                    <i className="bi bi-geo-alt pe-1"></i>{" "}
                                                    {game.location?.name}
                                                </p>
                                                <ul className="avatar-group list-unstyled align-items-center mb-0">
                                                    {(() => {
                                                        const attendingPlayers = game.players?.filter(p => p.attending) || [];

                                                        const displayed = attendingPlayers.slice(0, 3);
                                                        const remaining = attendingPlayers.length - displayed.length;

                                                        return (
                                                            <>
                                                                {displayed.map((p, idx) => (
                                                                    <li key={`${p.id}-${idx}`} className="avatar avatar-xs">
                                                                        <img
                                                                            className="avatar-img rounded-circle"
                                                                            src={
                                                                                p.participant_logo ||
                                                                                `/static/images/kb_static/lemont_rec_leagues_circle.png`
                                                                            }
                                                                            alt={p.participant_name}
                                                                        />
                                                                    </li>
                                                                ))}

                                                                {remaining > 0 && (
                                                                    <li className="avatar avatar-xs">
                                                                        <div className="avatar-img rounded-circle bg-primary">
                                                                            <span className="smaller text-white position-absolute top-50 start-50 translate-middle">
                                                                                +{remaining}
                                                                            </span>
                                                                        </div>
                                                                    </li>
                                                                )}

                                                                <li className="ms-3">
                                                                    <small>
                                                                        {attendingPlayers.length}{' '}
                                                                        {attendingPlayers.length === 1 ? 'attending' : 'attending'}
                                                                    </small>
                                                                </li>
                                                            </>
                                                        );
                                                    })()}
                                                </ul>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Container>
                </Card.Body>
            </Card>
        </Container>
    );
}

export default ManagerGameListPage;
